devLog('Loading Rails Vite Vue application...');

import { createApp } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import { Quasar, Ripple, ClosePopup } from 'quasar';

import 'quasar/dist/quasar.css';
import '@quasar/extras/material-icons/material-icons.css';

import App from '../client/src/App.vue';
import routes from '../client/src/router/routes.js';
import useAuthStore from '../client/src/stores/auth.js';
import { useLoginDialog } from '../client/src/composables/useLoginDialog.js';

import '../client/src/css/app.scss';
import '../client/src/css/tailwind.css';
import { devErrLog, devLog } from '../client/src/utils/logger.js';

// Import critical font files
import bebasRegular from '../client/src/assets/fonts/Bebas-Neue-Pro-Regular-BF66cf3d7814160.ttf';
import bebasExpandedBold from '../client/src/assets/fonts/Bebas-Neue-Pro-Expanded-Bold-BF66cf3d77b066f.ttf';
import bebasExpandedExtraBold from '../client/src/assets/fonts/Bebas-Neue-Pro-Expanded-ExtraBold-BF66cf3d793166e.ttf';
import bebasExpandedReg from '../client/src/assets/fonts/Bebas-Neue-Pro-Expanded-Regular-BF66cf3d77b3b0c.ttf';
import bebasSemiExpandedBoldItalic from '../client/src/assets/fonts/italic/Bebas-Neue-Pro-SemiExpanded-Bold-Italic-BF66cf3d78af7b5.ttf';
import bebasBold from '../client/src/assets/fonts/Bebas-Neue-Pro-Bold-BF66cf3d7770dfe.ttf';
import ledDotMatrix from '../client/src/assets/fonts/LED Dot-Matrix.ttf';

devLog('Dependencies loaded, creating app...');

// Function to preload fonts
const preloadFonts = (fontUrls, type = 'font/ttf') => {
	const promises = fontUrls.map((fontUrl) => {
		return new Promise((resolve) => {
			const link = document.createElement('link');
			link.rel = 'preload';
			link.as = 'font';
			link.type = type;
			link.href = fontUrl;
			link.crossOrigin = 'anonymous';
			link.onload = () => {
				devLog(`Font loaded: ${fontUrl}`);
				resolve(fontUrl);
			};
			link.onerror = () => {
				devErrLog(`Font failed to load: ${fontUrl}`);
				resolve(fontUrl); // Resolve to avoid blocking
			};
			document.head.appendChild(link);
		});
	});
	return Promise.all(promises);
};

if ('serviceWorker' in navigator) {
	window.addEventListener('load', () => {
		const swPath = '/service-worker.js';

		navigator.serviceWorker
			.register(swPath)
			.then((reg) => console.log('✅ Service Worker registered:', reg))
			.catch((err) => console.error('❌ SW registration failed:', err));
	});
}

window.deferredInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (event) => {
	event.preventDefault();
	window.deferredInstallPrompt = event;
	window.dispatchEvent(new Event('a2hs-available'));
});

// Wait for fonts to load before mounting app
document.addEventListener('DOMContentLoaded', async () => {
	const loadedFonts = await preloadFonts([
		bebasExpandedBold,
		bebasRegular,
		bebasBold,
		bebasExpandedExtraBold,
		bebasExpandedReg,
		bebasSemiExpandedBoldItalic,
		ledDotMatrix
	], 'font/ttf');
	devLog('🚀 ~ Loaded fonts:', loadedFonts);

	if (loadedFonts) {
		const appElement = document.getElementById('app');
		if (!appElement) {
			devErrLog('App element not found');
			return;
		}

		const router = createRouter({
			history: createWebHashHistory('/'),
			routes,
		});

		const protectedPaths = ['/picmysix', '/fixture', '/leaderboard', '/myAccount'];

		const INITIAL_LOAD_KEY = 'app_initial_redirect_performed';

		async function checkHasCompletedSelection(userId) {
			try {
				// 1. Get the current game week info
				const infoResponse = await fetch('/leaderboard/weekly_prize_info', {
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': window.csrfToken
					}
				});

				if (!infoResponse.ok) return false;
				const infoData = await infoResponse.json();

				const weekNumber = infoData?.game_week?.week_number;
				if (!weekNumber) return false;

				// 2. Get the user's bets for that specific week
				const betsResponse = await fetch(`/game_week_bets/${weekNumber}?user_id=${userId}`, {
					method: 'GET',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': window.csrfToken
					},
				});

				if (!betsResponse.ok) return false;
				const betsData = await betsResponse.json();

				// Return true only if they have exactly 6 players
				return betsData.players && betsData.players.length === 6;

			} catch (error) {
				devErrLog('Error checking completion status:', error);
				return false; // Fail safe
			}
		}
		router.beforeEach(async (to, from, next) => {
			devLog('🛣️ Navigation guard - attempting to access:', to.path);

			// Check if we have already performed the initial redirect check in this session
			const hasRedirected = sessionStorage.getItem(INITIAL_LOAD_KEY);
			let authState = useAuthStore.getState();

			if (from.matched.length === 0) {
				// Check if user has a cookie but auth state is not set (email verification scenario)
				const userEmailCookie = document.cookie
					.split('; ')
					.find((row) => row.startsWith('user_email='))
					?.split('=')[1];

				if (userEmailCookie && (!authState.isAuthenticated || !authState.user)) {
					devLog('⏳ Cookie found but auth state not loaded, checking auth...');
					try {
						await authState.checkAuth();
						authState = useAuthStore.getState();
					} catch (error) {
						devErrLog('Error during auth check in router guard:', error);
					}
				} else if (authState.loading) {
					devLog('⏳ Auth is loading, waiting for authentication to complete...');
					try {
						await authState.checkAuth();
						authState = useAuthStore.getState();
					} catch (error) {
						devErrLog('Error during auth check in router guard:', error);
					}
				}
			}

			// Only check and set the redirect flag if user is authenticated
			if (!hasRedirected && to.path === '/' && authState.isAuthenticated && authState.user) {
				const hasSixPlayers = await checkHasCompletedSelection(authState.user.id);

				if (hasSixPlayers) {
					devLog('🔄 Initial load redirect to /picmysix');
					// Mark as done so refreshes on '/' stay on '/'
					sessionStorage.setItem(INITIAL_LOAD_KEY, 'true');
					return next('/picmysix');
				}
				// Mark as done after checking (even if no redirect needed)
				sessionStorage.setItem(INITIAL_LOAD_KEY, 'true');
			} else if (!hasRedirected && authState.isAuthenticated && authState.user) {
				// If they land on any other page and are authenticated, mark as done
				sessionStorage.setItem(INITIAL_LOAD_KEY, 'true');
			}

			const isProtectedRoute =
				protectedPaths.some((path) => to.path.startsWith(path)) ||
				to.path.startsWith('/fixture-detail/');

			devLog('🔒 Is protected route?', isProtectedRoute);

			if (isProtectedRoute) {
				const authState = useAuthStore.getState();

				if (authState.isAuthenticated && authState.user) {
					devLog('✅ User already authenticated, allowing access to', to.path);
					next();
					return;
				}

				if (authState.isAuthenticated && !authState.user) {
					devLog('🚨 SECURITY: Invalid auth state - authenticated but no user details, logging out');
					authState.logout();
					const { showDialog } = useLoginDialog();
					showDialog(to.fullPath);
					next({ path: '/' });
					return;
				}

				const userEmailCookie = document.cookie
					.split('; ')
					.find((row) => row.startsWith('user_email='))
					?.split('=')[1];

				devLog('🍪 User email cookie:', userEmailCookie);

				if (!userEmailCookie) {
					devLog('🔒 No cookie found, showing login dialog');
					authState.logout();
					const { showDialog } = useLoginDialog();
					showDialog(to.fullPath);
					next({ path: '/' });
					return;
				}

				if (!authState.isAuthenticated) {
					devLog('🔄 Cookie found but auth state not set, triggering auth check...');
					try {
						await authState.checkAuth();
						const updatedAuthState = useAuthStore.getState();

						if (updatedAuthState.isAuthenticated && updatedAuthState.user) {
							devLog('✅ Authentication confirmed, allowing access to', to.path);
							next();
							return;
						} else {
							devLog('🔒 Authentication failed or no user details, showing login dialog');
							if (updatedAuthState.isAuthenticated && !updatedAuthState.user) {
								devLog('🚨 SECURITY: Invalid auth state after check - authenticated but no user, logging out');
								updatedAuthState.logout();
							}
							const { showDialog } = useLoginDialog();
							showDialog(to.fullPath);
							next({ path: '/' });
							return;
						}
					} catch (error) {
						devErrLog('Authentication check failed:', error);
						const updatedAuthState = useAuthStore.getState();
						if (updatedAuthState.isAuthenticated) {
							updatedAuthState.logout();
						}
						const { showDialog } = useLoginDialog();
						showDialog(to.fullPath);
						next({ path: '/' });
						return;
					}
				}
			}

			next();
		});

		const app = createApp(App);

		app.use(Quasar, {
			plugins: {},
			directives: {
				Ripple,
				ClosePopup,
			},
		});

		app.use(router);

		const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
		window.csrfToken = csrfToken;

		devLog('Mounting app...');
		app.mount('#app');

		devLog('App mounted successfully');
	}
});